import { CATEGORY_IMAGES } from '../../shared/assets/categoryImages';
import {
  MAX_PORTFOLIO_IMAGES,
  providersService,
} from '../../shared/services/providers.service';
import { templateForCategoryCode } from './onboardingTemplates';

export interface SeedDefaultPortfolioArgs {
  categoryCode: string | null | undefined;
  /** URLs already attached to the portfolio — skipped to keep the call idempotent. */
  existingUrls?: Iterable<string>;
  /** Max number of bundled images to upload (default 5). Clamped down
   *  further to the remaining slots under {@link MAX_PORTFOLIO_IMAGES}
   *  so a top-up never makes the backend reject the last image. */
  limit?: number;
}

export interface SeedDefaultPortfolioResult {
  added: number;
  /** True when every candidate URL was already in the portfolio (nothing to do). */
  skippedAlreadyPresent: boolean;
  /** True when no bundled images exist for the resolved category slug. */
  noImagesAvailable: boolean;
  /** First per-image upload error, if any — surfaced to callers as a soft warning. */
  firstError: unknown;
}

/**
 * Upload bundled category-matched images into the current provider's portfolio.
 * Used by:
 *   - the onboarding flows (fires once at pro account creation)
 *   - the "Photos par défaut" dashboard button (fallback / top-up)
 *
 * Failures on individual uploads don't abort the rest; the caller decides
 * whether the surfaced {@link SeedDefaultPortfolioResult} should turn into
 * a user-visible error or be ignored silently.
 */
export async function seedDefaultPortfolioImages(
  args: SeedDefaultPortfolioArgs,
): Promise<SeedDefaultPortfolioResult> {
  const slug = templateForCategoryCode(args.categoryCode).imageSlug;
  const pool = CATEGORY_IMAGES[slug] ?? [];
  if (pool.length === 0) {
    return { added: 0, skippedAlreadyPresent: false, noImagesAvailable: true, firstError: null };
  }

  const existing = new Set(args.existingUrls ?? []);
  const candidates = pool.filter((url) => !existing.has(url));
  // The backend caps the portfolio at MAX_PORTFOLIO_IMAGES; uploading more
  // than the remaining slots would just produce 422 errors for the tail
  // images. Clamp the requested batch size accordingly.
  const remainingSlots = Math.max(0, MAX_PORTFOLIO_IMAGES - existing.size);
  const requested = args.limit ?? 5;
  const toUpload = candidates.slice(0, Math.min(requested, remainingSlots));
  if (toUpload.length === 0) {
    return { added: 0, skippedAlreadyPresent: true, noImagesAvailable: false, firstError: null };
  }

  let added = 0;
  let firstError: unknown = null;
  for (const url of toUpload) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) continue;
      const blob = await resp.blob();
      const filename = url.split('/').pop()?.split('?')[0] ?? `auto-${Date.now()}.jpg`;
      const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
      const uploaded = await providersService.uploadPortfolioImage(file);
      await providersService.addPortfolioImage({ imageUrl: uploaded.url });
      added += 1;
    } catch (err) {
      if (firstError == null) firstError = err;
      // eslint-disable-next-line no-console
      console.warn('[seedDefaultPortfolioImages] upload failed for', url, err);
    }
  }
  return { added, skippedAlreadyPresent: false, noImagesAvailable: false, firstError };
}
